"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardCheck, PackageOpen, SearchCheck, Sparkles, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  title: "Analysis",
  description: "We conduct a deep-dive audit of your current training frameworks and BPO requirements to identify efficiency gaps and skill development opportunities.",
  icon: ClipboardCheck
}, {
  title: "Transition",
  description: "Our experts manage the seamless migration of processes, ensuring zero downtime while integrating our specialized talent into your workflow.",
  icon: PackageOpen
}, {
  title: "Execution",
  description: "We deploy our high-performance teams and training modules, utilizing Electric Indigo standards to drive immediate operational excellence.",
  icon: Sparkles
}, {
  title: "Optimization",
  description: "Continuous monitoring and iterative feedback loops ensure your BPO services and staff skills evolve alongside global industry benchmarks.",
  icon: SearchCheck
}];
export default function ThePerfectProtocol() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalScrollableDistance = rect.height;
      const currentScrollPosition = windowHeight / 2 - rect.top;
      let percentage = currentScrollPosition / totalScrollableDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="222853" id="the-integration-process" ref={sectionRef} className="dark bg-background text-foreground relative min-h-screen">
      {}
      <div className="sticky top-0 z-50 bg-background/40 backdrop-blur-xl border-b border-white/10 py-4 px-6 shadow-md">
        <div className="max-w-4xl mx-auto flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg md:text-xl font-default font-bold tracking-tight text-foreground">
              The Integration Process
            </h2>
            <span className="text-sm font-mono text-secondary">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-1.5 bg-muted" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
        <div className="text-center mb-24">
          <h3 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tighter">
            Precision Integration, <br className="hidden md:block" /> Scalable Excellence.
          </h3>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default leading-relaxed">
            From training excellence to scalable BPO. Since 2017, we have cultivated a methodology that transforms professional training into tangible results. We are now integrating BPO outsourcing into our service line, delivering scalable, precision-driven solutions that convert capability into real-world performance.
          </p>
        </div>

        {}
        <div className="relative border-l-2 border-secondary/20 ml-4 md:ml-12 space-y-32 pb-24">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative pl-8 md:pl-20 min-h-[30vh] flex flex-col justify-center group">
                {}
                <div className="absolute -left-[11px] top-1/2 -translate-y-1/2 size-5 rounded-lg bg-background border-4 border-secondary transition-transform duration-500 group-hover:scale-125 shadow-lg shadow-secondary/20" />
                
                {}
                <span className="absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 text-[8rem] md:text-[14rem] font-mono font-bold text-secondary/5 select-none leading-none pointer-events-none transition-colors duration-500 group-hover:text-secondary/10">
                  {i + 1}
                </span>

                <Card className="relative z-10 border-white/10 shadow-xl bg-background/40 backdrop-blur-xl overflow-visible rounded-lg">
                  <CardHeader className="px-0 pt-0 pb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 rounded-md bg-secondary text-secondary-foreground shadow-md">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="text-2xl md:text-3xl font-default font-bold text-foreground tracking-tight">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    <p className="text-muted-foreground text-lg leading-relaxed max-w-xl font-default">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center relative z-20">
          <Button asChild size="lg" className="rounded-md px-8 bg-secondary text-secondary-foreground hover:scale-105 transition-transform shadow-lg shadow-secondary/20">
            <Link to="/BpoServicesPage">
              Start Your Transition
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}